import type { InfiniteData } from '@tanstack/react-query'

import type { PostTypeFields } from '@tribeplatform/gql-client/graphql'
import type {
  PostType,
  MutationUnarchivePostTypeFieldArgs,
  PaginatedPostType,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { getPostTypesKey, getPostTypeKey } from '../../utils/keys/post.key.js'

export const useUnarchivePostTypeField = (options?: {
  fields?: PostTypeFields
  useMutationOptions?: UseMutationOptions<
    PostType,
    Error,
    MutationUnarchivePostTypeFieldArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const postTypesKey = getPostTypesKey()
  const networkKey = getNetworkKey()

  return useMutation<PostType, Error, MutationUnarchivePostTypeFieldArgs>(
    input => client.postType.unarchiveField(input, fields),
    {
      onMutate: variables => {
        const postTypeKey = getPostTypeKey({ variables: { id: variables?.id } })

        queryClient.cancelQueries(postTypesKey)
        queryClient.cancelQueries(postTypeKey)

        queryClient.setQueriesData<PostType>(postTypeKey, old => ({
          ...old,
          postFields: {
            fields: old?.postFields?.fields?.map(field => ({
              ...field,
              archived: field?.key === variables?.key ? false : field?.archived,
            })),
          },
        }))

        queryClient.setQueriesData<InfiniteData<PaginatedPostType>>(
          postTypesKey,
          old => ({
            ...old,
            pages: old?.pages?.map(page => ({
              ...page,
              edges: page?.edges?.map(edge => ({
                ...edge,
                node: {
                  ...edge?.node,
                  postFields: {
                    fields: edge?.node?.postFields?.fields?.map(field => ({
                      ...field,
                      archived:
                        field?.key === variables?.key ? false : field?.archived,
                    })),
                  },
                },
              })),
            })),
          }),
        )
      },
      onSettled: variables => {
        const postTypeKey = getPostTypeKey({ variables: { id: variables?.id } })

        queryClient.invalidateQueries(postTypesKey)
        queryClient.invalidateQueries(postTypeKey)
        queryClient.invalidateQueries(networkKey)
      },
      ...useMutationOptions,
    },
  )
}
